import { delay } from "@prismicio/editor-support/Promise"
import { cache, useRequest } from "@prismicio/editor-support/Suspense"
import clsx from "clsx"
import { useState } from "react"

import { type SX, theme } from "../../theme/new"
import styles from "./Image.module.css"

export interface ImageProps {
  "aria-label"?: string
  src: string
  alt?: string
  loading?: "eager" | "lazy"
  decoding?: "async" | "auto" | "sync"
  sizing?: "contain" | "cover" | "none"
  borderRadius?: 0 | 4 | 8
  boxShadow?: "3"
  animateOnLoad?: boolean
  sx?: SX
  onLoad?: () => void
  background?: "transparent"
}

/*
 * A Basic Image that can play an enter animation when it loads.
 */
export function ImageComponent(props: ImageProps) {
  const {
    ["aria-label"]: ariaLabel,
    src,
    alt,
    onLoad,
    sizing = "none",
    borderRadius = 0,
    boxShadow,
    animateOnLoad = true,
    sx,
    background,

    ...otherProps
  } = props

  const [isLoaded, setIsLoaded] = useState(false)

  const loadingState = getLoadingState({ animateOnLoad, isLoaded })

  function onImageLoad() {
    setIsLoaded(true)
    onLoad?.()
  }

  return (
    <img
      {...otherProps}
      aria-label={ariaLabel}
      className={clsx(
        styles.noSelect,
        sizing !== "none" && styles[`sizing-${sizing}`],
        borderRadius !== 0 && styles[`border-radius-${borderRadius}`],
        loadingState && styles[loadingState],
        background && styles[`background-${background}`]
      )}
      style={{ ...sx, boxShadow: boxShadow && theme.boxShadow[boxShadow] }}
      src={src}
      alt={alt}
      onLoad={onImageLoad}
    />
  )
}

function getLoadingState(args: { animateOnLoad: boolean; isLoaded: boolean }) {
  const { animateOnLoad, isLoaded } = args

  if (!animateOnLoad) return undefined

  return isLoaded ? "loaded" : "loading"
}

/*
 * An image that suspends till its src is in the browser cache.
 * Use this if you want to wait for all images to be loaded before rendering a suspense-aware parent component.
 */
export function SuspenseImage(props: ImageProps) {
  const { src, ...otherProps } = props

  useRequest(prefetcher, [src])

  return <ImageComponent {...otherProps} src={src} />
}

function prefetcher(src: string) {
  // While it's nicer to wait for all images,
  // let's not block the entire component if a single image can't load.
  const timeout = delay(10_000).then(() => 1)

  const imageLoaded = new Promise<1>((resolve) => {
    const img = new Image()
    img.onload = () => resolve(1)
    img.src = src
  })

  return Promise.race([timeout, imageLoaded])
}

cache.registerFetcherConfig(prefetcher, { cacheCapacity: 10_000 })
